import { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import keycloak from '../keycloak/keycloak';

interface HeaderProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Header({ collapsed = false, onToggleCollapse }: HeaderProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    localStorage.removeItem('access_token');
    if (keycloak && typeof keycloak.logout === 'function') {
      keycloak.logout({
        redirectUri: window.location.origin,
      });
      console.log('Logging out...');
    } else {
      alert('Logout is not available. Keycloak is not initialized.');
      console.error('Keycloak logout is not available.');
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
        zIndex: (theme) => theme.zIndex.drawer - 1
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 64, sm: 70 } }}>
        {/* Left side - Hamburger Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onToggleCollapse && (
            <IconButton
              onClick={onToggleCollapse}
              sx={{
                display: { xs: 'none', lg: 'flex' },
                color: '#64748b',
                '&:hover': {
                  bgcolor: '#f8fafc'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>

        {/* Right side - Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        
          {/* Profile */}
          <Tooltip title="Account settings" arrow>
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
              sx={{
                ml: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontWeight: 700,
                  fontSize: '14px'
                }}
              >
                JD
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 240,
              mt: 1.5,
              borderRadius: 2,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                borderRadius: 1,
                mx: 1,
                my: 0.5
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* User Info */}
          <Box sx={{ px: 2, py: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'primary.main',
                  fontWeight: 700
                }}
              >
                JD
              </Avatar>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  John Doe
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                  john.doe@company.com
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          <MenuItem
            onClick={handleLogout}
            sx={{
              color: 'error.main',
              '&:hover': {
                bgcolor: 'error.50'
              }
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Logout
            </Typography>
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 320,
              maxWidth: 400,
              mt: 1.5,
              borderRadius: 2
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
          </Box>

          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {[
              {
                id: 1,
                title: 'Model Deployed Successfully',
                description: 'GPT-4 Turbo is now live and ready to use',
                time: '5 minutes ago',
                type: 'success'
              },
              {
                id: 2,
                title: 'New Model Available',
                description: 'Claude 3 Opus has been added to the catalog',
                time: '1 hour ago',
                type: 'info'
              },
              {
                id: 3,
                title: 'Deployment Failed',
                description: 'Gemini Pro deployment encountered an error',
                time: '2 hours ago',
                type: 'error'
              }
            ].map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={handleNotificationClose}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  whiteSpace: 'normal'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor:
                        notification.type === 'success'
                          ? 'success.main'
                          : notification.type === 'error'
                          ? 'error.main'
                          : 'info.main'
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                    {notification.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', mb: 0.5 }}>
                  {notification.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.time}
                </Typography>
              </MenuItem>
            ))}
          </Box>

          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              View All Notifications
            </Typography>
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
